import { jsPDF } from 'jspdf';

/**
 * Generates a branded gift certificate PDF and triggers a download.
 * All rendering is done with jsPDF primitives (no external images/fonts)
 * so it works offline and stays under ~10 KB.
 *
 * @param {object} cert
 * @param {string} cert.recipientName
 * @param {string} cert.sessionLength   e.g. "60-Minute"
 * @param {number} cert.value           e.g. 110
 * @param {string} cert.message         personal note
 * @param {string} cert.occasion
 * @param {string} cert.code            e.g. "AS-GC-2026-A7X9"
 * @param {string} cert.issuedOn        ISO date string
 * @param {string} cert.validThrough    ISO date string
 * @param {string} cert.buyerName       optional — "From:"
 */
export const generateGiftCertificatePDF = (cert) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Palette
  const teal = [15, 118, 110];      // teal-700
  const tealLight = [204, 251, 241]; // teal-100
  const rose = [251, 207, 232];      // rose-200
  const ink = [31, 41, 55];          // gray-800
  const muted = [107, 114, 128];     // gray-500

  // Outer decorative border
  doc.setDrawColor(...teal);
  doc.setLineWidth(3);
  doc.roundedRect(24, 24, pageW - 48, pageH - 48, 12, 12, 'S');

  // Inner subtle border
  doc.setDrawColor(...tealLight);
  doc.setLineWidth(1);
  doc.roundedRect(40, 40, pageW - 80, pageH - 80, 8, 8, 'S');

  // Corner accents (small rose dots)
  doc.setFillColor(...rose);
  [
    [56, 56], [pageW - 56, 56], [56, pageH - 56], [pageW - 56, pageH - 56],
  ].forEach(([x, y]) => doc.circle(x, y, 3, 'F'));

  // Header — brand block
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...teal);
  doc.text('AYUMI SUZUKI MASSAGE THERAPY', pageW / 2, 90, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text('CERTIFIED · MIDTOWN SACRAMENTO · 2528 I STREET', pageW / 2, 106, { align: 'center' });

  // Divider
  doc.setDrawColor(...teal);
  doc.setLineWidth(0.5);
  doc.line(pageW / 2 - 40, 118, pageW / 2 + 40, 118);

  // Main title — "Gift Certificate"
  doc.setFont('times', 'italic');
  doc.setFontSize(48);
  doc.setTextColor(...ink);
  doc.text('Gift Certificate', pageW / 2, 175, { align: 'center' });

  // "Presented to"
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...muted);
  doc.text('PRESENTED TO', pageW / 2, 210, { align: 'center', charSpace: 2 });

  // Recipient name
  doc.setFont('times', 'normal');
  doc.setFontSize(32);
  doc.setTextColor(...teal);
  doc.text(cert.recipientName || 'Valued Recipient', pageW / 2, 245, { align: 'center' });

  // Recipient underline
  const nameWidth = Math.max(180, doc.getTextWidth(cert.recipientName || 'Valued Recipient'));
  doc.setDrawColor(...tealLight);
  doc.setLineWidth(0.75);
  doc.line(pageW / 2 - nameWidth / 2 - 20, 255, pageW / 2 + nameWidth / 2 + 20, 255);

  // Session line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...muted);
  doc.text('IS ENTITLED TO ONE', pageW / 2, 285, { align: 'center', charSpace: 2 });

  // Session (bold)
  doc.setFont('times', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...ink);
  doc.text(`${cert.sessionLength} Massage Session`, pageW / 2, 315, { align: 'center' });

  // Value
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...teal);
  doc.text(`$${cert.value} GIFT VALUE`, pageW / 2, 335, { align: 'center', charSpace: 2 });

  // Personal message (optional, wrapped)
  if (cert.message && cert.message.trim().length > 0) {
    doc.setFont('times', 'italic');
    doc.setFontSize(12);
    doc.setTextColor(...muted);
    const lines = doc.splitTextToSize(`"${cert.message.trim()}"`, pageW - 200);
    doc.text(lines, pageW / 2, 375, { align: 'center' });
  }

  // Signature area — left
  const sigY = pageH - 110;
  doc.setDrawColor(...ink);
  doc.setLineWidth(0.5);
  doc.line(80, sigY, 260, sigY);
  doc.setFont('times', 'italic');
  doc.setFontSize(14);
  doc.setTextColor(...teal);
  doc.text('Ayumi Suzuki', 170, sigY - 8, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text('AYUMI SUZUKI · CAMTC', 170, sigY + 14, { align: 'center', charSpace: 1 });

  // Certificate metadata — right
  const metaY = sigY - 40;
  const metaX = pageW - 260;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...teal);
  doc.text('CERTIFICATE NO.', metaX, metaY, { charSpace: 1 });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...ink);
  doc.text(cert.code, metaX, metaY + 14);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...teal);
  doc.text('ISSUED', metaX, metaY + 32, { charSpace: 1 });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...ink);
  doc.text(cert.issuedOn, metaX, metaY + 46);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...teal);
  doc.text('VALID THROUGH', metaX + 120, metaY + 32, { charSpace: 1 });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...ink);
  doc.text(cert.validThrough, metaX + 120, metaY + 46);

  // Footer — redemption note
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text(
    'To redeem: book at ayumisuzuki.com/book or text 415-678-9133 and mention this certificate number.',
    pageW / 2,
    pageH - 50,
    { align: 'center' }
  );
  doc.text(
    'Non-refundable · Transferable · One-year validity from issue date',
    pageW / 2,
    pageH - 38,
    { align: 'center' }
  );

  // Filename
  const safeName = (cert.recipientName || 'Recipient').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
  doc.save(`Gift-Certificate-${safeName}-${cert.code}.pdf`);
};

/**
 * Deterministic short code from timestamp — human-friendly for the certificate.
 * Example: AS-GC-2026-A7X9
 */
export const generateCertificateCode = () => {
  const year = new Date().getFullYear();
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no confusing chars
  let suffix = '';
  for (let i = 0; i < 4; i += 1) {
    suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `AS-GC-${year}-${suffix}`;
};

/** Format an ISO date -> "Feb 14, 2026" */
export const formatCertDate = (d) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
